'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { Check, Info } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { getSectionMotionHeading } from '@/components/dynamic-zone/section-motion-heading';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { serviceTypes } from '@/lib/constants/pricing-data';
// import { components } from '@/types/strapi';

// interface Pricing {
// 	heading: string;
// 	sub_heading: string;
// 	products: components['schemas']['Product'];
// }
// const plans_:components["schemas"]["ItemsPlanItemComponent"][];
export function PricingComparisonSlider({
	isFirst = false,
}: { isFirst?: boolean } = {}) {
	const SectionHeading = getSectionMotionHeading(isFirst);
	const [isYearly, setIsYearly] = useState(false);
	const [activeService, setActiveService] = useState('transactional');
	return (
		<TooltipProvider>
			<section
				id="pricing"
				className="pricing-comparison-slider-section bg-gradient-to-b from-gray-50 to-white py-8">
				<noscript>
					<style>{`
						.pricing-comparison-slider-section h2[style],
						.pricing-comparison-slider-section h3[style],
						.pricing-comparison-slider-section p[style],
						.pricing-comparison-slider-section [style*='opacity'],
						.pricing-comparison-slider-section [style*='translateY'],
						.pricing-comparison-slider-section [style*='translateX'] {
							opacity: 1 !important;
							transform: none !important;
						}
					`}</style>
				</noscript>
				<div className="container mx-auto px-4">
					<div className="mx-auto mb-4 max-w-3xl text-center">
						<SectionHeading
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							className="mb-3 text-4xl font-bold tracking-tight md:text-5xl">
							Simple, Transparent Pricing
							{/* {props.heading} */}
						</SectionHeading>
						<motion.p
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ delay: 0.1 }}
							className="text-xl text-gray-600/90">
							Choose the perfect plan for your communication needs
							{/* {props.sub_heading} */}
						</motion.p>
					</div>

					<Tabs
						defaultValue="transactional"
						className="w-full"
						onValueChange={setActiveService}>
						<TabsList className="flex h-auto flex-wrap justify-center border-0 bg-transparent">
							{serviceTypes.map(service => (
								<TabsTrigger
									id={service.label
										.replace(/\s+/g, '-')
										.toLowerCase()}
									key={service.id}
									value={service.id}
									className={`relative px-3 text-base font-medium transition-all focus:outline-none data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-600 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:scale-105 data-[state=inactive]:hover:text-gray-900`}>
									{service.label}
									{activeService === service.id && (
										<motion.div
											layoutId="activeTab"
											className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
											initial={false}
											transition={{
												type: 'spring',
												stiffness: 500,
												damping: 30,
											}}
										/>
									)}
								</TabsTrigger>
							))}
						</TabsList>

						<div className="mx-auto mt-2 flex w-fit items-center justify-center gap-4 rounded-full border border-gray-100 bg-gray-50/50 px-6 py-3">
							<Label
								htmlFor="billing-switch"
								className={`text-sm font-medium ${!isYearly ? 'text-gray-900' : 'text-gray-500'}`}>
								Monthly
							</Label>
							<Switch
								id="billing-switch"
								checked={isYearly}
								onCheckedChange={setIsYearly}
								className="data-[state=checked]:bg-blue-500"
							/>
							<Label
								htmlFor="billing-switch"
								className={`flex items-center text-sm font-medium ${isYearly ? 'text-gray-900' : 'text-gray-500'}`}>
								Yearly
								<Badge
									variant="secondary"
									className="ml-2 bg-blue-50 text-blue-600 hover:bg-blue-100">
									Save 10%
								</Badge>
							</Label>
						</div>

						{serviceTypes.map(service => (
							<TabsContent
								key={service.id}
								value={service.id}
								className="mt-6">
								<div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-3">
									<AnimatePresence mode="wait">
										{service.plans.map((plan, index) => (
											<motion.div
												key={plan.name}
												initial={{
													opacity: 0,
													y: 20,
												}}
												whileInView={{
													opacity: 1,
													y: 0,
												}}
												viewport={{
													once: true,
												}}
												transition={{
													delay: index * 0.1,
												}}
												className="relative h-full">
												<Card
													className={`relative h-full transition-all duration-300 hover:border-blue-500 ${plan.color} border ${
														plan.popular
															? 'border-blue-200 shadow-xl'
															: 'border-gray-100 shadow-lg'
													} ${plan.shadowColor} group hover:scale-[1.02] hover:shadow-2xl`}>
													{plan.popular && (
														<div className="absolute -top-4 left-0 right-0 flex justify-center">
															<Badge className="bg-blue-500 px-4 py-1 text-sm font-medium hover:bg-blue-600">
																Most Popular
															</Badge>
														</div>
													)}

													<CardHeader className="pb-8 pt-8">
														<div
															className={`mb-6 flex h-14 w-14 items-center justify-center rounded-xl ${plan.iconColor} bg-white/80 shadow-sm`}>
															<plan.icon className="h-7 w-7" />
														</div>
														<h3 className="mb-2 text-2xl font-bold">
															{plan.name}
														</h3>
														<p className="text-gray-600/90">
															{plan.description}
														</p>
													</CardHeader>

													<CardContent className="pb-8">
														<div className="mb-8">
															<div className="flex items-baseline gap-2">
																{plan.monthlyPrice !==
																null ? (
																	<>
																		<span className="text-5xl font-bold">
																			₹
																			{isYearly
																				? plan.yearlyPrice
																				: plan.monthlyPrice}
																		</span>
																		<span className="text-gray-600/90">
																			/month
																		</span>
																	</>
																) : (
																	<span className="text-3xl font-bold text-gray-800">
																		Custom
																		Pricing
																	</span>
																)}
															</div>
															<div className="mt-3 flex items-center justify-between text-sm text-gray-600/90">
																{plan.pricePerMessage !==
																null ? (
																	<span>
																		₹
																		{
																			plan.pricePerMessage
																		}{' '}
																		per
																		message
																	</span>
																) : (
																	<span>
																		Contact
																		for
																		pricing
																		details
																	</span>
																)}
																{plan.minQuantity && (
																	<Tooltip>
																		<TooltipTrigger>
																			<div className="flex items-center gap-1 hover:text-gray-900">
																				<Info className="h-4 w-4" />
																				{plan.minQuantity ===
																				'Custom'
																					? 'Custom Volume'
																					: `Min. ${plan.minQuantity}`}
																			</div>
																		</TooltipTrigger>
																		<TooltipContent>
																			<p>
																				{plan.minQuantity ===
																				'Custom'
																					? 'Contact us for custom volume requirements'
																					: `Minimum ${plan.minQuantity} per month`}
																			</p>
																		</TooltipContent>
																	</Tooltip>
																)}
															</div>
															{plan.validity && (
																<div className="mt-2 text-sm text-gray-600/90">
																	<span className="font-medium">
																		Validity:
																	</span>{' '}
																	{
																		plan.validity
																	}
																</div>
															)}
														</div>

														<div className="space-y-6">
															{/* Volume and Free Messages - Only show for RCS */}
															{service.id ===
																'rcs' &&
																plan.volume &&
																plan.freeMessages && (
																	<div className="rounded-lg bg-white/50 p-4">
																		<h4 className="mb-2 font-semibold">
																			Volume
																			&
																			Free
																			Messages
																		</h4>
																		<p className="text-sm text-gray-600">
																			Volume:{' '}
																			{
																				plan.volume
																			}
																		</p>
																		<p className="text-sm text-gray-600">
																			Free:{' '}
																			{
																				plan.freeMessages
																			}
																		</p>
																	</div>
																)}

															{/* Message Pricing - Only show for RCS */}
															{service.id ===
																'rcs' &&
																plan.messagePricing && (
																	<div className="rounded-lg bg-white/50 p-4">
																		<h4 className="mb-2 font-semibold">
																			Message
																			Types
																			&
																			Pricing
																		</h4>
																		{Object.entries(
																			plan.messagePricing,
																		).map(
																			([
																				type,
																				price,
																			]) => (
																				<div
																					key={
																						type
																					}
																					className="flex justify-between py-1 text-sm">
																					<span className="text-gray-600">
																						{
																							type
																						}
																					</span>
																					<span className="font-medium">
																						{
																							price
																						}
																					</span>
																				</div>
																			),
																		)}
																	</div>
																)}

															{/* Other Charges - Only show for RCS */}
															{service.id ===
																'rcs' &&
																plan.otherCharges && (
																	<div className="rounded-lg bg-white/50 p-4">
																		<h4 className="mb-2 font-semibold">
																			Other
																			Charges
																		</h4>
																		{Object.entries(
																			plan.otherCharges,
																		).map(
																			([
																				charge,
																				value,
																			]) => (
																				<div
																					key={
																						charge
																					}
																					className="flex justify-between py-1 text-sm">
																					<span className="text-gray-600">
																						{
																							charge
																						}
																					</span>
																					<span className="font-medium">
																						{
																							value
																						}
																					</span>
																				</div>
																			),
																		)}
																	</div>
																)}

															{/* Features - Show for all services */}
															<div className="space-y-3">
																<p className="font-semibold">
																	Features
																</p>
																{plan.features.map(
																	(
																		feature,
																		featureIndex,
																	) => (
																		<motion.div
																			key={
																				featureIndex
																			}
																			initial={{
																				opacity: 0,
																				x: -20,
																			}}
																			whileInView={{
																				opacity: 1,
																				x: 0,
																			}}
																			viewport={{
																				once: true,
																			}}
																			transition={{
																				delay:
																					index *
																						0.1 +
																					featureIndex *
																						0.1,
																			}}
																			className="flex items-center gap-3">
																			<Check className="h-5 w-5 flex-shrink-0 text-green-500" />
																			<span className="text-gray-600/90">
																				{
																					feature
																				}
																			</span>
																		</motion.div>
																	),
																)}
															</div>
														</div>
													</CardContent>

													<CardFooter className="pb-8">
														<Link
															href="/contact-us"
															passHref
															legacyBehavior>
															<Button
																asChild
																className={`w-full border border-blue-100 bg-white text-blue-500 shadow-sm transition-all duration-300 hover:bg-blue-500 hover:text-white group-hover:border-transparent group-hover:bg-blue-500 group-hover:text-white`}
																size="lg">
																<a>
																	Get Started
																</a>
															</Button>
														</Link>
													</CardFooter>
												</Card>
											</motion.div>
										))}
									</AnimatePresence>
								</div>
							</TabsContent>
						))}
					</Tabs>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ delay: 0.4 }}
						className="mt-6 text-center text-gray-600/90">
						<p className="mx-auto max-w-2xl">
							All plans include access to our API, documentation,
							and basic support.
							<br />
							Need a custom plan?{' '}
							<Link href="/contact-us" passHref legacyBehavior>
								<Button
									asChild
									variant="link"
									className="p-0 font-medium text-blue-500 hover:text-blue-600">
									<a>Contact our sales team</a>
								</Button>
							</Link>{' '}
							for volume-based pricing.
						</p>
					</motion.div>
				</div>
			</section>
		</TooltipProvider>
	);
}
